import { useContext } from 'react'
import { AppContext } from '../app'

/**
 * Hook to check user permissions
 * Returns helper functions to check if user has specific permissions
 */
export const usePermissions = () => {
  const { user } = useContext(AppContext)
  const permissions = user?.selectedContext?.permissions || []

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission)
  }

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every((perm) => permissions.includes(perm))
  }

  /**
   * Check if user has at least one of the specified permissions
   */
  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.some((perm) => permissions.includes(perm))
  }

  /**
   * Shorthand permission checks for common operations
   */
  const can = {
    create: (resource: string) => hasPermission(`${resource}.create`),
    read: (resource: string) => hasPermission(`${resource}.read`),
    update: (resource: string) => hasPermission(`${resource}.update`),
    delete: (resource: string) => hasPermission(`${resource}.delete`),
    manage: (resource: string) => hasPermission(`${resource}.manage`)
  }

  return {
    permissions,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    can
  }
}
